import { useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet } from "lucide-react";

const QUICK = [500, 1000, 2500, 5000];

export default function WalletRechargeDialog({ balance, onCredited }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("500");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const v = Number(amount);
    if (!Number.isFinite(v) || v < 100) return toast.error("Minimum top-up is ₹100.");
    setBusy(true);
    try {
      const { data } = await api.post("/exhibitor/wallet/topup/create-order", { amount: v });
      const rzp = data.razorpay_order;
      const complete = async (paymentId, signature) => {
        const verify = await api.post("/exhibitor/wallet/topup/verify", {
          order_id: data.order_id,
          razorpay_order_id: rzp.id,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
          amount: v,
        });
        toast.success(`Wallet recharged with ₹${v.toLocaleString("en-IN")}. New balance ₹${verify.data.balance.toLocaleString("en-IN")}.`);
        setOpen(false);
        onCredited?.(verify.data);
      };
      // Mock flow (no real Razorpay keys yet) — same pattern as checkout.
      if (rzp.id.startsWith("order_mock_") || !window.Razorpay) {
        await complete(`pay_mock_${Date.now()}`, "sig_mock");
        return;
      }
      const options = {
        key: data.razorpay_key_id, amount: rzp.amount, currency: "INR", order_id: rzp.id,
        name: "CityShor Wallet", description: `Top-up ₹${v.toLocaleString("en-IN")}`,
        handler: (res) => complete(res.razorpay_payment_id, res.razorpay_signature),
        modal: { ondismiss: () => setBusy(false) },
      };
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start top-up");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="wallet-recharge-btn"
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-[#F48E2E] hover:text-[#D97815] font-medium underline underline-offset-2"
      >
        Recharge
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="wallet-recharge-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-[#F48E2E]" /> Recharge wallet</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-600 mb-3">
            Current balance <b>₹{Number(balance || 0).toLocaleString("en-IN")}</b>. Wallet funds are used for Sponsored slot boosts. Minimum ₹100 per top-up.
          </div>
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Quick amounts</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {QUICK.map((q) => (
              <button
                key={q}
                type="button"
                data-testid={`wallet-quick-${q}`}
                onClick={() => setAmount(String(q))}
                className={`px-3 h-8 rounded-full text-xs font-medium transition-colors ${String(q) === amount ? "bg-[#F48E2E] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              >
                ₹{q.toLocaleString("en-IN")}
              </button>
            ))}
          </div>
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Amount (₹)</div>
          <Input data-testid="wallet-amount" type="number" min="100" step="100" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="wallet-pay-submit"
              disabled={busy}
              onClick={submit}
              className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]"
            >
              {busy ? "Processing…" : `Pay ₹${Number(amount || 0).toLocaleString("en-IN")}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
